// Loadable.js
// Author's note (Quinn Rohlf):
// This file was ported in from one of my other projects with express permission
// from Cullen King. Its inclusion in the rwgps-ui codebase does not provide
// any intellectual property claims over the scout-maps codebase, in whole or in
// part, for Ride with GPS over the scout-maps codebase.

// MIT License

// Copyright (c) 2018 Quinn Rohlf

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import React, { Fragment } from 'react'
import _ from 'lodash'
import styles from './Loadable.scss'
import { CircleSpinner } from 'common/spinners/CircleSpinner'

// initial state - dependencies are not loaded, and loading has not
// been initiated
const STATE_INITIAL = 'STATE_INITIAL'
// loading state - dependencies are not loaded, but loading is pending
const STATE_LOADING = 'STATE_LOADING'
// loaded state - all dependencies have successfully loaded
const STATE_LOADED = 'STATE_LOADED'
// special error case - the resource was not found
const STATE_404 = 'STATE_404'
// special error case - resource is forbidden
const STATE_403 = 'STATE_403'
// error state - some kind of error was encountered while loading dependencies
const STATE_ERROR = 'STATE_ERROR'

// if we don't have something useful from the backend
const ERR_MSG_GENERIC = 'Something caused an application error to occur while loading this page. Our team has been notified of this issue and will investigate.'

// do this as a constant so that we don't trigger unnecessary PureComponent re-renders
const EMPTY_OBJ = {}

export class Loadable extends React.PureComponent {
  static defaultProps = {
    flashDelay: 600 // see https://github.com/jamiebuilds/react-loadable#avoiding-flash-of-loading-component
  }

  constructor (props) {
    super(props)
    // resourceLoaded can take three forms:
    // - `true` allows us to short-circuit all logic around resource loading
    //   and just immediately render the correct component with no flash
    // - () => Promise() is the async form that allows us to load the resource
    const resourceLoad = this.props.resourceLoaded === true
      ? STATE_LOADED
      : STATE_INITIAL
    this.state = {
      component: this.props.component,
      pastDelay: props.flashDelay === 0, // see https://github.com/jamiebuilds/react-loadable#avoiding-flash-of-loading-component
      resourceLoad,
      resources: null,
      pageLoad: STATE_LOADED, // we don't do dynamic imports yet
      resourceError: null
    }
  }

  componentDidMount () {
    const { resourceLoad } = this.state
    const { resourceLoaded, preloadResources, flashDelay } = this.props
    this._delayTimer = setTimeout(this.onDelayComplete, flashDelay) // see https://github.com/jamiebuilds/react-loadable#avoiding-flash-of-loading-component
    preloadResources && preloadResources() // preloadResources is fire-and-forget
    if (resourceLoad === STATE_INITIAL) {
      this.setState({ resourceLoad: STATE_LOADING })
      // this needs to be wrapped in try/catch for proper error handling in the
      // callback form of resourceLoaded()
      try {
        const maybePromise = resourceLoaded(this.onResourceUpdate)
        if (maybePromise && maybePromise.then) {
          maybePromise
            .catch(this.onResourceError) // this needs to come first so we don't eat render errors!
            .then((resources) => this.onResourceUpdate(resources))
        }
      } catch (e) {
        this.onResourceError(e)
      }
    }
  }

  // because of the .catch .then order of the promise resolvers above,
  // we need to check that we haven't previously errored here before
  // we set STATE_LOADED
  onResourceUpdate = (resources) => {
    const { resourceError, pageLoad } = this.state
    if (resourceError || pageLoad === STATE_ERROR) {
      return
    }

    this.setState({
      resourceLoad: STATE_LOADED,
      resources
    })
  }

  onResourceError = (e) => {
    console.error('error while loading Loadable:')
    console.error(e)
    const errorCode = _.get(e, 'errors[0].error_code') || _.get(e, 'details[0].error_code')
    switch (errorCode) {
      case 'not_found':
        this.setState({ resourceLoad: STATE_404, resourceError: e })
        break
      case 'forbidden':
        this.setState({ resourceLoad: STATE_403, resourceError: e })
        break
      default:
        this.setState({ resourceLoad: STATE_ERROR, resourceError: e })
    }
  }

  onDelayComplete = () => {
    this.setState({ pastDelay: true })
  }

  render () {
    const { resourceLoad, resources, pageLoad, pastDelay, resourceError } = this.state
    const {
      component: Component,
      resourceLoaded,
      errorComponent,
      matchesErrorCondition,
      loadingText,
      ...props
    } = this.props

    if (matchesErrorCondition && resourceError && matchesErrorCondition(resourceError)) {
      return errorComponent(resourceError, props)
    }

    if (pageLoad === STATE_ERROR) {
      return <LoadableCentered
        title='Application Error'
        detail={ERR_MSG_GENERIC} />
    }

    if (resourceLoad === STATE_404) {
      return <LoadableCentered
        title='404 Not found'
        detail='We were unable to locate the requested resource.' />
    }

    if (resourceLoad === STATE_403) {
      return <LoadableCentered
        title='403 Forbidden'
        detail='You do not have permission to access the requested resource.' />
    }

    if (resourceLoad === STATE_ERROR) {
      return <LoadableCentered
        title='Resource Load Error'
        detail={ERR_MSG_GENERIC} />
    }

    if (resourceLoad === STATE_LOADED && pageLoad === STATE_LOADED) {
      return <Component loadableResources={resources || EMPTY_OBJ} {...props} />
    }

    if (pastDelay) return <LoadableCentered loading loadingText={loadingText} />

    return null
  }
}

export const LoadableCentered = ({ title, detail, loading, loadingText }) => (
  <div className={styles.LoadableCentered}>
    <h1>{title}</h1>
    <p>{detail}</p>
    {loading && <Fragment>
      <CircleSpinner centered size={30} />
      <p data-light>{loadingText}</p>
    </Fragment>}
  </div>
)
